import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  doc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  getDoc,
  Timestamp,
  updateDoc,
  runTransaction
} from 'firebase/firestore';
import { db } from '../../firebase/firebase';

const HOLDING_ORDER_STATUSES = ['accepted', 'in_progress', 'completed'];
const REQUIRED_AUTO_CONFIRM_FIELDS = ['date', 'startTime', 'endTime', 'sellerUid', 'buyerUid', 'jobId'];

const toJSDate = (value) => {
  if (!value) return null;
  const date = value.seconds ? new Date(value.seconds * 1000) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const timeToMinutes = (time) => {
  if (!time || typeof time !== 'string') return null;
  const [hours, minutes] = time.split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
};

const isSameCalendarDate = (a, b) => {
  const first = toJSDate(a);
  const second = toJSDate(b);
  if (!first || !second) return false;
  return (
    first.getFullYear() === second.getFullYear() &&
    first.getMonth() === second.getMonth() &&
    first.getDate() === second.getDate()
  );
};

const rangesOverlap = (startA, endA, startB, endB) => {
  const aStart = timeToMinutes(startA);
  const aEnd = timeToMinutes(endA);
  const bStart = timeToMinutes(startB);
  const bEnd = timeToMinutes(endB);
  if ([aStart, aEnd, bStart, bEnd].some((value) => value === null)) return false;
  return aStart < bEnd && aEnd > bStart;
};

const isTimeInsideRange = (start, end, rangeStart, rangeEnd) => {
  const startMin = timeToMinutes(start);
  const endMin = timeToMinutes(end);
  const rangeStartMin = timeToMinutes(rangeStart);
  const rangeEndMin = timeToMinutes(rangeEnd);
  if ([startMin, endMin, rangeStartMin, rangeEndMin].some((value) => value === null)) return false;
  return startMin >= rangeStartMin && endMin <= rangeEndMin && startMin < endMin;
};

const getDayKeys = (dateValue) => {
  const date = toJSDate(dateValue);
  if (!date) return [];
  const names = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const name = names[date.getDay()];
  return [name, name.slice(0, 3)];
};

const isTimestampSnapshotEqual = (a, b) => {
  if (!a && !b) return true;
  return (a?.seconds || null) === (b?.seconds || null);
};

const isScheduleSnapshotEqual = (snapshot = {}, settings = {}) => {
  const snapDuration = snapshot.scheduleDuration || {};
  const latestDuration = settings.scheduleDuration || {};
  const snapTime = snapshot.acceptedBookingTime || {};
  const latestTime = settings.acceptedBookingTime || {};
  return (
    JSON.stringify(snapshot.recurringDays || []) === JSON.stringify(settings.recurringDays || []) &&
    snapTime.start === latestTime.start &&
    snapTime.end === latestTime.end &&
    isTimestampSnapshotEqual(snapDuration.startDate, latestDuration.startDate) &&
    isTimestampSnapshotEqual(snapDuration.endDate, latestDuration.endDate)
  );
};

const isPaymentSnapshotEqual = (snapshot = [], latest = []) =>
  JSON.stringify(snapshot.map((method) => method?.id || method?.method || '').sort()) ===
  JSON.stringify(latest.map((method) => method?.id || method?.method || '').sort());

const isOrderInsideSchedule = (order, scheduleSettings = {}) => {
  const date = toJSDate(order.date);
  const { recurringDays = [], acceptedBookingTime = {}, scheduleDuration = {} } = scheduleSettings;
  const activeStart = toJSDate(scheduleDuration.startDate);
  const activeEnd = toJSDate(scheduleDuration.endDate);
  const dayKeys = getDayKeys(date);

  if (!date || !acceptedBookingTime.start || !acceptedBookingTime.end || !activeStart || !activeEnd) return false;
  if (date < new Date(activeStart.setHours(0, 0, 0, 0)) || date > new Date(activeEnd.setHours(23, 59, 59, 999))) return false;
  if (!recurringDays.map((day) => String(day).toLowerCase()).some((day) => dayKeys.includes(day))) return false;
  return isTimeInsideRange(order.startTime, order.endTime, acceptedBookingTime.start, acceptedBookingTime.end);
};

const hasPricingForOrder = (order, job = {}) => {
  if (order.compensationType === 'free' || job.compensationType === 'free') return true;
  if (order.rateType === 'project' || order.projectPrice > 0) return Number(order.projectPrice || job.projectPrice || 0) > 0;
  return Number(order.price || job.price || 0) > 0;
};

const hasUnavailableConflict = (order, unavailabilities = []) =>
  unavailabilities.some((item) => {
    const orderDate = toJSDate(order.date);
    if (!orderDate) return false;
    const start = item.start ? new Date(item.start) : null;
    const end = item.indefinite ? new Date(orderDate.getFullYear() + 1, orderDate.getMonth(), orderDate.getDate()) : item.end ? new Date(item.end) : null;
    if (!start || !end) return false;
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return orderDate >= start && orderDate <= end;
  });

const getOrderConflict = (order, existingOrders = [], scheduleSettings = {}) => {
  const totalBufferMinutes = (scheduleSettings.bufferBetweenBookings || 0) + (scheduleSettings.travelTime || 0);

  const sameDayOrders = existingOrders.filter((item) =>
    item.orderId !== order.orderId &&
    isSameCalendarDate(item.date, order.date)
  );

  if (scheduleSettings.limitBookings && sameDayOrders.some((item) => HOLDING_ORDER_STATUSES.includes(item.status))) {
    return { reason: 'limit_bookings' };
  }

  const holdingConflict = sameDayOrders.find((item) =>
    HOLDING_ORDER_STATUSES.includes(item.status) &&
    rangesOverlap(order.startTime, order.endTime, item.startTime, item.endTime)
  );
  if (holdingConflict) return { reason: 'order_overlap', conflict: holdingConflict };

  const importedConflict = sameDayOrders.find((item) =>
    item.status === 'imported' &&
    rangesOverlap(order.startTime, order.endTime, item.startTime, item.endTime)
  );
  if (importedConflict) return { reason: 'imported_calendar_conflict', conflict: importedConflict };

  if (totalBufferMinutes > 0) {
    const startMin = timeToMinutes(order.startTime);
    const bufferedConflict = sameDayOrders.find((item) => {
      if (!HOLDING_ORDER_STATUSES.includes(item.status)) return false;
      const endMin = timeToMinutes(item.endTime);
      return startMin !== null && endMin !== null && startMin >= endMin && startMin < endMin + totalBufferMinutes;
    });
    if (bufferedConflict) return { reason: 'buffer_or_travel_conflict', conflict: bufferedConflict };
  }

  return null;
};

const notifyUser = async (uid, notification) => {
  if (!uid) return;
  const docRef = doc(db, 'users', uid, 'data', 'notifications');
  await runTransaction(db, async (transaction) => {
    const docSnap = await transaction.get(docRef);
    const notifications = [];
    if (docSnap.exists()) {
      let i = 0;
      while (docSnap.data()[i]) {
        notifications.push(docSnap.data()[i]);
        i++;
      }
    }
    const exists = notification.dedupeKey && notifications.some((item) => item.dedupeKey === notification.dedupeKey);
    if (exists) return;
    notifications.push({ ...notification, createdAt: Date.now() });
    transaction.set(docRef, { ...notifications });
  });
};

const formatBookedDateKey = (value) => {
  const date = toJSDate(value);
  return date ? date.toISOString().split('T')[0] : null;
};

const addBookedDateSlots = async (order) => {
  if (!order?.sellerUid || !order.startTime || !order.endTime) return;
  const dates = Array.isArray(order.selectedDays) && order.selectedDays.length > 0
    ? order.selectedDays
    : [order.date];

  await Promise.all(dates.map(async (dateValue) => {
    const dateKey = formatBookedDateKey(dateValue);
    if (!dateKey) return;
    const bookedDateRef = doc(db, 'users', order.sellerUid, 'bookedDates', dateKey);
    const bookedDateDoc = await getDoc(bookedDateRef);
    const slot = {
      startTime: order.startTime,
      endTime: order.endTime,
      type: 'booked',
      description: 'Client appointment',
      orderId: order.orderId
    };

    if (bookedDateDoc.exists()) {
      const existingSlots = Array.isArray(bookedDateDoc.data()?.timeSlots)
        ? bookedDateDoc.data().timeSlots
        : [];
      await updateDoc(bookedDateRef, { timeSlots: [...existingSlots, slot] });
    } else {
      await setDoc(bookedDateRef, { timeSlots: [slot] });
    }
  }));
};

// Function to check if 2 weeks (336 hours) have passed since order creation
const hasLateFeeEligibility = (orderCreated) => {
  if (!orderCreated) return false;
  
  const createdTime = orderCreated.toDate ? orderCreated.toDate() : new Date(orderCreated);
  const currentTime = new Date();
  const timeDifference = currentTime - createdTime;
  const hoursDifference = timeDifference / (1000 * 60 * 60);
  
  return hoursDifference >= 336;
};

// Function to calculate late fee amount
const calculateLateFeeAmount = (order) => {
  if (!order?.lateFee || order.lateFee === '0' || order.lateFee === '') {
    return 0;
  }
  
  const lateFeeRate = parseFloat(order.lateFee) / 100;
  const baseAmount = order.price || 0;
  
  return baseAmount * lateFeeRate;
};

const isDefaultProfilePhotoURL = (photoURL = '') => photoURL.includes('profileimage');

export const areOrderReviewsAllowed = (order = {}) => {
  if (order.reviewsAllowed !== undefined) return order.reviewsAllowed !== false;
  if (order.reviewEnabled !== undefined) return order.reviewEnabled !== false;
  if (order.reviewAccepted !== undefined) return order.reviewAccepted !== false;
  if (order.sellerAllowsReviewsAtPurchase !== undefined) return order.sellerAllowsReviewsAtPurchase !== false;
  return true;
};

const getUserProfilePhotoURL = async (uid, snapshotPhotoURL = '') => {
  if (snapshotPhotoURL && !isDefaultProfilePhotoURL(snapshotPhotoURL)) return snapshotPhotoURL;
  if (!uid) return '';

  const [profileSnap, userDataSnap] = await Promise.all([
    getDoc(doc(db, 'users', uid, 'data', 'profile')),
    getDoc(doc(db, 'users', uid, 'data', 'userdata'))
  ]);

  return profileSnap.data()?.url || userDataSnap.data()?.photoURL || '';
};

export const applyLateFeeToOrder = createAsyncThunk(
  'orders/applyLateFeeToOrder',
  async ({ orderId }) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      const orderDoc = await getDoc(orderRef);
      
      if (!orderDoc.exists()) {
        throw new Error('Order not found');
      }
      
      const orderData = orderDoc.data();
      
      // Check if late fee is already applied
      if (orderData.lateFeeApplied) {
        return { orderId, lateFeeApplied: true };
      }
      
      // Check if 2 weeks (336 hours) have passed
      if (!hasLateFeeEligibility(orderData.created)) {
        return { orderId, lateFeeApplied: false, reason: 'Not eligible yet' };
      }
      
      // Calculate late fee amount
      const lateFeeAmount = calculateLateFeeAmount(orderData);
      
      if (lateFeeAmount > 0) {
        // Update order with late fee
        await updateDoc(orderRef, {
          lateFeeApplied: true,
          lateFeeAmount: lateFeeAmount,
          lateFeeAppliedAt: new Date(),
          totalWithLateFee: (orderData.price || 0) + lateFeeAmount
        });
        
        return { 
          orderId, 
          lateFeeApplied: true, 
          lateFeeAmount,
          totalWithLateFee: (orderData.price || 0) + lateFeeAmount
        };
      }
      
      return { orderId, lateFeeApplied: false, reason: 'No late fee configured' };
    } catch (error) {
      console.error('Error applying late fee:', error);
      throw error;
    }
  }
);

export const checkAndApplyLateFees = createAsyncThunk(
  'orders/checkAndApplyLateFees',
  async (_, { getState }) => {
    try {
      const { orders } = getState();
      
      const pendingOrders = orders.bookings.filter(order => 
        order.status === 'pending' && 
        !order.lateFeeApplied &&
        hasLateFeeEligibility(order.created)
      );
      
      const results = [];
      
      for (const order of pendingOrders) {
        const result = await applyLateFeeToOrder({ orderId: order.orderId });
        results.push(result);
      }
      
      return results;
    } catch (error) {
      console.error('Error checking and applying late fees:', error);
      throw error;
    }
  }
);

export const createBooking = createAsyncThunk(
  'orders/createBooking',
  async (payload) => {
    try {
      const orderRef = doc(collection(db, 'orders'));
      const reviewsAllowed = areOrderReviewsAllowed(payload);
      const now = Timestamp.now();
      const orderId = orderRef.id;
      const latestJobSnap = payload.jobId ? await getDoc(doc(db, 'jobs', payload.jobId)) : null;
      const latestJob = latestJobSnap?.exists() ? latestJobSnap.data() : null;
      const sellerSettingsSnap = payload.sellerUid
        ? await getDoc(doc(db, 'users', payload.sellerUid, 'calendarSettings', 'settings'))
        : null;
      const sellerSettings = sellerSettingsSnap?.exists() ? sellerSettingsSnap.data() : {};
      const selectedJobIds = Array.isArray(sellerSettings.selectedJobsAuto)
        ? sellerSettings.selectedJobsAuto.map((job) => job?.value || job)
        : [];
      const jobAutoConfirmEnabled =
        latestJob?.autoConfirmEnabled === true ||
        latestJob?.autoConfirm === true ||
        latestJob?.autoConfirm === 'true' ||
        latestJob?.scheduleSettings?.autoConfirmEnabled === true ||
        (sellerSettings.autoConfirmEnabled === true &&
          (sellerSettings.autoConfirmAll === true || selectedJobIds.includes(payload.jobId)));

      const baseOrder = {
        ...payload,
        orderId,
        reviewsAllowed,
        sellerAllowsReviewsAtPurchase: reviewsAllowed,
        autoConfirmAttempted: jobAutoConfirmEnabled,
        autoConfirmStatus: jobAutoConfirmEnabled ? 'checking' : 'off'
      };

      const blockAutoConfirm = async (reason, details = null) => {
        const blockedOrder = {
          ...baseOrder,
          status: 'pending',
          confirmed: false,
          autoConfirmStatus: 'blocked',
          autoConfirmBlockedReason: reason,
          autoConfirmBlockedAt: now,
          autoConfirmConflictDetails: details
        };
        await setDoc(orderRef, blockedOrder);
        await notifyUser(payload.sellerUid, {
          icon: 'warning',
          color: '#f59e0b',
          name: payload.buyerName || 'Buyer',
          text: 'sent a booking that needs review',
          to: `/seller/orders?orderId=${orderId}`,
          targetRole: 'seller',
          targetPage: 'orders',
          targetId: orderId,
          senderUid: payload.buyerUid,
          dedupeKey: `auto-confirm-blocked-${orderId}`,
          read: false
        });
        return { orderId, status: 'pending', autoConfirmStatus: 'blocked', autoConfirmBlockedReason: reason };
      };

      if (!jobAutoConfirmEnabled) {
        await setDoc(orderRef, baseOrder);
        return { orderId, status: baseOrder.status, autoConfirmStatus: 'off' };
      }

      if (payload.buyerUid && payload.buyerUid === payload.sellerUid) {
        return blockAutoConfirm('self_booking');
      }

      const missingField = REQUIRED_AUTO_CONFIRM_FIELDS.find((field) => !payload[field]);
      if (missingField) {
        return blockAutoConfirm('missing_required_order_fields', { missingField });
      }

      if (!latestJob) {
        return blockAutoConfirm('job_not_found');
      }

      if (!payload.jobScheduleSnapshot) {
        return blockAutoConfirm('missing_order_snapshot', { field: 'scheduleSettings' });
      }

      if (!isScheduleSnapshotEqual(payload.jobScheduleSnapshot, latestJob.scheduleSettings || {})) {
        return blockAutoConfirm('schedule_changed');
      }

      if (!isOrderInsideSchedule(payload, latestJob.scheduleSettings || {})) {
        return blockAutoConfirm('outside_schedule');
      }

      if (!hasPricingForOrder(payload, latestJob)) {
        return blockAutoConfirm('missing_pricing');
      }

      const isPaidJob = (payload.compensationType || latestJob.compensationType || 'paid') !== 'free';
      const latestPaymentMethods = latestJob.paymentMethods || [];
      if (isPaidJob && latestPaymentMethods.length === 0) {
        return blockAutoConfirm('missing_payment_method');
      }

      if (isPaidJob && (!Array.isArray(payload.paymentMethods) || payload.paymentMethods.length === 0)) {
        return blockAutoConfirm('missing_order_snapshot', { field: 'paymentMethods' });
      }

      if (isPaidJob && !Array.isArray(payload.jobPaymentMethodsSnapshot)) {
        return blockAutoConfirm('missing_order_snapshot', { field: 'jobPaymentMethodsSnapshot' });
      }

      if (isPaidJob && !isPaymentSnapshotEqual(payload.jobPaymentMethodsSnapshot, latestPaymentMethods)) {
        return blockAutoConfirm('payment_methods_changed');
      }

      if (typeof payload.reviewsAllowed !== 'boolean' || typeof payload.jobReviewsAllowedSnapshot !== 'boolean') {
        return blockAutoConfirm('missing_order_snapshot', { field: 'reviewsAllowed' });
      }

      if (payload.jobReviewsAllowedSnapshot !== (latestJob.reviewAccepted !== false)) {
        return blockAutoConfirm('review_permission_changed');
      }

      const [ordersSnap, unavailabilitiesSnap] = await Promise.all([
        getDocs(query(collection(db, 'orders'), where('sellerUid', '==', payload.sellerUid))),
        getDocs(collection(db, 'users', payload.sellerUid, 'unavailabilities'))
      ]);
      const existingOrders = ordersSnap.docs.map((orderDoc) => orderDoc.data());
      const unavailabilities = unavailabilitiesSnap.docs.map((unavailabilityDoc) => unavailabilityDoc.data());

      if (hasUnavailableConflict(payload, unavailabilities)) {
        return blockAutoConfirm('unavailable_date');
      }

      const conflict = getOrderConflict(payload, existingOrders, latestJob.scheduleSettings || {});
      if (conflict) {
        return blockAutoConfirm(conflict.reason, conflict.conflict ? {
          orderId: conflict.conflict.orderId,
          title: conflict.conflict.jobHeadline || conflict.conflict.jobTitle,
          startTime: conflict.conflict.startTime,
          endTime: conflict.conflict.endTime,
          calendarName: conflict.conflict.calendarName || ''
        } : null);
      }

      const acceptedOrder = {
        ...baseOrder,
        status: 'accepted',
        confirmed: true,
        autoConfirmStatus: 'accepted',
        autoConfirmedAt: now,
        activityTime: now
      };

      await setDoc(orderRef, acceptedOrder);
      await addBookedDateSlots(acceptedOrder);
      await notifyUser(payload.sellerUid, {
        icon: 'check_circle',
        color: '#53F550',
        name: payload.buyerName || 'Buyer',
        text: 'had a booking automatically accepted',
        to: `/seller/orders?orderId=${orderId}`,
        targetRole: 'seller',
        targetPage: 'orders',
        targetId: orderId,
        senderUid: payload.buyerUid,
        dedupeKey: `auto-confirm-seller-accepted-${orderId}`,
        read: false
      });
      await notifyUser(payload.buyerUid, {
        icon: 'check_circle',
        color: '#53F550',
        name: payload.sellerName || 'Seller',
        text: 'automatically accepted your booking',
        to: `/buyer/orders?orderId=${orderId}`,
        targetRole: 'buyer',
        targetPage: 'orders',
        targetId: orderId,
        senderUid: payload.sellerUid,
        dedupeKey: `auto-confirm-accepted-${orderId}`,
        read: false
      });

      return { orderId, status: 'accepted', autoConfirmStatus: 'accepted' };
    } catch (error) {
      console.error('Error creating booking:', error);
      throw error;
    }
  }
);


export const fetchCompletedBookingsBySeller = createAsyncThunk(
  'orders/fetchCompletedBookingsBySeller',
  async (sellerUid) => {
    try {
      const completedBookings = [];
      const ref = collection(db, 'orders');
      const q = query(ref, where('sellerUid', '==', sellerUid), where('status', '==', 'completed'));
      const snap = await getDocs(q);

      snap.forEach((doc) => {
        completedBookings.push({ ...doc.data() });
      });

      return completedBookings; // Return the array of completed bookings
    } catch (error) {
      console.error('Error fetching completed bookings:', error);
      throw error;
    }
  }
);

export const fetchViewedSellerCompletedBookings = createAsyncThunk(
  'orders/fetchViewedSellerCompletedBookings',
  async (sellerUid, { rejectWithValue }) => {
    try {
      const completedBookings = [];
      const ref = collection(db, 'orders');
      const q = query(ref, where('sellerUid', '==', sellerUid), where('status', '==', 'completed'));
      const snap = await getDocs(q);

      snap.forEach((doc) => {
        completedBookings.push({ ...doc.data() });
      });

      return completedBookings;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchJobsInProgressBySeller = createAsyncThunk(
  "orders/fetchJobsInProgressBySeller",
  async (sellerUid) => {
    try {
      const ref = collection(db, "orders");
      const [acceptedSnap, inProgressSnap] = await Promise.all([
        getDocs(query(ref, where("sellerUid", "==", sellerUid), where("status", "==", "accepted"))),
        getDocs(query(ref, where("sellerUid", "==", sellerUid), where("status", "==", "in_progress")))
      ]);

      const jobsInProgress = acceptedSnap.size + inProgressSnap.size;

      return jobsInProgress; // Return count of jobs in progress
    } catch (error) {
      console.error("Error fetching jobs in progress:", error);
      throw error;
    }
  }
);


export const fetchBookingsByQuery = createAsyncThunk(
  'orders/fetchPurchasesByQuery',
  async (payload, { rejectWithValue }) => {
    try {
      const orders = [];
      const ref = collection(db, 'orders');
      const q = query(ref, where('sellerUid', '==', payload)); // Filter by Seller
      const snap = await getDocs(q);
      snap.forEach((doc) => {
        orders.push({ ...doc.data() });
      });
      return orders;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchViewedSellerJobsInProgress = createAsyncThunk(
  "orders/fetchViewedSellerJobsInProgress",
  async (sellerUid, { rejectWithValue }) => {
    try {
      const ref = collection(db, "orders");
      const [acceptedSnap, inProgressSnap] = await Promise.all([
        getDocs(query(ref, where("sellerUid", "==", sellerUid), where("status", "==", "accepted"))),
        getDocs(query(ref, where("sellerUid", "==", sellerUid), where("status", "==", "in_progress")))
      ]);

      return acceptedSnap.size + inProgressSnap.size;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchViewedSellerBookings = createAsyncThunk(
  'orders/fetchViewedSellerBookings',
  async (sellerUid, { rejectWithValue }) => {
    try {
      const orders = [];
      const ref = collection(db, 'orders');
      const q = query(ref, where('sellerUid', '==', sellerUid));
      const snap = await getDocs(q);
      snap.forEach((doc) => {
        orders.push({ ...doc.data() });
      });
      return orders;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPurchasesByQuery = createAsyncThunk(
  'orders/fetchOrdersByQuery',
  async (payload) => {
    try {
      const purchases = [];
      const ref = collection(db, 'orders');
      const q = query(ref, where('buyerUid', '==', payload)); // Filter by Buyer
      const snap = await getDocs(q);
      snap.forEach((doc) => {
        purchases.push({ ...doc.data() });
      });
      return purchases;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const changeConfirmedStatus = createAsyncThunk(
  'orders/changeConfirmedStatus',
  async ({ orderId, confirmed }) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      setDoc(orderRef, { confirmed: confirmed }, { merge: true });
      return { confirmed: confirmed, orderId: orderId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const changeOrderStatus = createAsyncThunk(
  'orders/changeOrderStatus',
  async ({ orderId, status, time }) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      await setDoc(orderRef, { status: status, activityTime: time }, { merge: true });
      return { status: status, activityTime: time, orderId: orderId };
    } catch (error) {
      return error;
    }
  }
);

export const changePurchaseStatus = createAsyncThunk(
  'orders/changePurchaseStatus',
  async ({ orderId, status, time }) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      await setDoc(orderRef, { status: status, activityTime: time }, { merge: true });
      return { status: status, activityTime: time, orderId: orderId };
    } catch (error) {
      return error;
    }
  }
);


// ── New status-machine thunks ─────────────────────────────────────────────

const makeStatusThunk = (name, fields) =>
  createAsyncThunk(name, async ({ orderId }, { rejectWithValue }) => {
    try {
      await setDoc(doc(db, 'orders', orderId), { ...fields, activityTime: Timestamp.now() }, { merge: true });
      return { orderId, ...fields };
    } catch (e) { return rejectWithValue(e.message); }
  });

const canBuyerCancelDirectly = (dateValue) => {
  if (!dateValue) return false;
  const orderDate = dateValue.seconds ? new Date(dateValue.seconds * 1000) : new Date(dateValue);
  if (Number.isNaN(orderDate.getTime())) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const bookingDay = new Date(orderDate);
  bookingDay.setHours(0, 0, 0, 0);

  return Math.ceil((bookingDay - today) / 86400000) >= 15;
};

export const acceptOrder    = makeStatusThunk('orders/acceptOrder',  { status: 'accepted',        confirmed: true });

export const acceptOrderLimited = createAsyncThunk(
  'orders/acceptOrderLimited',
  async ({ orderId, order }, { rejectWithValue }) => {
    try {
      await setDoc(doc(db, 'orders', orderId),
        { status: 'accepted', confirmed: true, activityTime: Timestamp.now() },
        { merge: true }
      );

      let limitBookings = false;
      let bufferMinutes = 0;
      let travelMinutes = 0;

      if (order?.jobId) {
        const jobSnap = await getDoc(doc(db, 'jobs', order.jobId));
        if (jobSnap.exists()) {
          const ss = jobSnap.data()?.scheduleSettings || {};
          limitBookings  = ss.limitBookings          ?? false;
          bufferMinutes  = ss.bufferBetweenBookings  ?? 0;
          travelMinutes  = ss.travelTime             ?? 0;
        }
      }

      const totalBuffer = bufferMinutes + travelMinutes;
      const needsCheck  = (limitBookings || totalBuffer > 0) && order?.date && order?.sellerUid;

      if (!needsCheck) return { orderId, status: 'accepted', confirmed: true, declinedOrderIds: [], conflictedOrders: [] };

      const snap = await getDocs(query(
        collection(db, 'orders'),
        where('sellerUid', '==', order.sellerUid)
      ));

      const targetDate = new Date(order.date.seconds * 1000);
      const [acceptedEndH, acceptedEndM] = order.endTime.split(':').map(Number);
      const acceptedEndMin = acceptedEndH * 60 + acceptedEndM;
      const bufferWindowEnd = acceptedEndMin + totalBuffer;

      const declinedOrderIds = [];
      const conflictedOrders = [];
      const promises = [];

      snap.forEach((docSnap) => {
        const o = docSnap.data();
        if (o.orderId === orderId) return;
        const isPending = o.status === 'pending';
        if (!isPending) return;

        const oDate = new Date(o.date.seconds * 1000);
        const sameDay =
          oDate.getDate()     === targetDate.getDate()     &&
          oDate.getMonth()    === targetDate.getMonth()    &&
          oDate.getFullYear() === targetDate.getFullYear();
        if (!sameDay) return;

        if (limitBookings) {
          declinedOrderIds.push(o.orderId);
          promises.push(setDoc(doc(db, 'orders', o.orderId),
            { status: 'declined', activityTime: Timestamp.now() },
            { merge: true }
          ));
        } else if (totalBuffer > 0 && o.jobId === order.jobId) {
          const [oStartH, oStartM] = o.startTime.split(':').map(Number);
          const oStartMin = oStartH * 60 + oStartM;
          const isInBufferWindow = oStartMin >= acceptedEndMin && oStartMin < bufferWindowEnd;
          if (isInBufferWindow) {
            const conflict = {
              orderId:       o.orderId,
              bufferConflict: true,
              conflictsWith: {
                orderId,
                startTime:     order.startTime,
                endTime:       order.endTime,
                bufferMinutes: totalBuffer,
              },
            };
            conflictedOrders.push(conflict);
            promises.push(setDoc(doc(db, 'orders', o.orderId),
              { bufferConflict: true, conflictsWith: conflict.conflictsWith, activityTime: Timestamp.now() },
              { merge: true }
            ));
          }
        }
      });

      await Promise.all(promises);
      return { orderId, status: 'accepted', confirmed: true, declinedOrderIds, conflictedOrders };
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

export const declineOrder   = makeStatusThunk('orders/declineOrder', { status: 'declined' });

export const autoDeclineExpiredOrders = createAsyncThunk(
  'orders/autoDeclineExpiredOrders',
  async (_, { getState }) => {
    const { order } = getState();
    const now = Date.now();
    const expired = order.bookings.filter(
      (b) => b.status === 'pending' && b.date?.seconds * 1000 < now
    );
    const declinedIds = [];
    for (const booking of expired) {
      await setDoc(
        doc(db, 'orders', booking.orderId),
        { status: 'declined', activityTime: Timestamp.now() },
        { merge: true }
      );
      declinedIds.push(booking.orderId);
    }
    return declinedIds;
  }
);
export const markStarted    = makeStatusThunk('orders/markStarted',  { status: 'in_progress' });
export const markCompleted = createAsyncThunk('orders/markCompleted', async ({ orderId }, { rejectWithValue }) => {
  try {
    const orderRef = doc(db, 'orders', orderId);
    const orderSnap = await getDoc(orderRef);
    if (!orderSnap.exists()) return rejectWithValue('Order not found');

    const orderData = orderSnap.data();
    const isFreeOrder = orderData.compensationType === 'free';
    const reviewsAllowed = areOrderReviewsAllowed(orderData);
    const updates = isFreeOrder
      ? {
          status: reviewsAllowed ? 'review_pending' : 'completed',
          paid: true,
          paymentStatus: 'not_required',
          paymentNotRequiredAt: Timestamp.now(),
          activityTime: Timestamp.now()
        }
      : {
          status: 'completed',
          activityTime: Timestamp.now()
        };

    await setDoc(orderRef, updates, { merge: true });
    return { orderId, ...updates };
  } catch (e) {
    return rejectWithValue(e.message);
  }
});
export const requestCancel  = makeStatusThunk('orders/requestCancel',{ status: 'action_required' });
export const cancelBooking = createAsyncThunk('orders/cancelBooking', async ({ orderId }, { rejectWithValue }) => {
  try {
    const orderRef = doc(db, 'orders', orderId);
    const orderSnap = await getDoc(orderRef);
    if (!orderSnap.exists()) return rejectWithValue('Order not found');

    const orderData = orderSnap.data();
    let updates;
    if (orderData.status === 'pending') {
      updates = { status: 'cancelled', cancelledBy: 'buyer' };
    } else if (orderData.status === 'accepted') {
      updates = canBuyerCancelDirectly(orderData.date)
        ? { status: 'cancelled', cancelledBy: 'buyer' }
        : { status: 'action_required' };
    } else {
      return rejectWithValue(`Cannot cancel buyer booking from status ${orderData.status}`);
    }

    await setDoc(orderRef, { ...updates, activityTime: Timestamp.now() }, { merge: true });
    return { orderId, ...updates };
  } catch (e) {
    return rejectWithValue(e.message);
  }
});
export const allowCancel    = makeStatusThunk('orders/allowCancel',  { status: 'cancelled', cancelledBy: 'buyer' });
export const denyCancel     = makeStatusThunk('orders/denyCancel',   { status: 'accepted' });
export const cancelOrder    = makeStatusThunk('orders/cancelOrder',  { status: 'cancelled', cancelledBy: 'seller' });
export const reportPayment = createAsyncThunk('orders/reportPayment', async ({ orderId, selectedPaymentMethod }, { rejectWithValue }) => {
  try {
    const paymentMethodSnapshot = selectedPaymentMethod
      ? JSON.parse(JSON.stringify(selectedPaymentMethod))
      : null;
    const updates = {
      paymentStatus: 'buyer_marked_paid',
      selectedPaymentMethodId: paymentMethodSnapshot?.id || '',
      selectedPaymentMethod: paymentMethodSnapshot,
      paidReportedAt: Timestamp.now(),
      activityTime: Timestamp.now(),
    };
    await setDoc(doc(db, 'orders', orderId), updates, { merge: true });
    return { orderId, ...updates };
  } catch (e) {
    return rejectWithValue(e.message);
  }
});
export const confirmPayment = createAsyncThunk('orders/confirmPayment', async ({ orderId }, { rejectWithValue }) => {
  try {
    const orderRef = doc(db, 'orders', orderId);
    const orderSnap = await getDoc(orderRef);
    if (!orderSnap.exists()) return rejectWithValue('Order not found');

    const reviewsAllowed = areOrderReviewsAllowed(orderSnap.data());
    const updates = {
      status: reviewsAllowed ? 'review_pending' : 'completed',
      paid: true,
      paymentStatus: 'confirmed',
      paidConfirmedAt: Timestamp.now(),
      activityTime: Timestamp.now(),
    };
    await setDoc(orderRef, updates, { merge: true });
    return { orderId, ...updates };
  } catch (e) { return rejectWithValue(e.message); }
});

export const archiveOrder = createAsyncThunk('orders/archiveOrder', async ({ orderId }, { rejectWithValue }) => {
  try {
    await setDoc(doc(db, 'orders', orderId), { buyerArchived: true }, { merge: true });
    return { orderId, buyerArchived: true };
  } catch (e) { return rejectWithValue(e.message); }
});

export const rateCompletedOrder = createAsyncThunk(
  'orders/rateCompletedOrder',
  async ({ orderId, ratings, comments, skillsets }, { rejectWithValue }) => {
    try {
      const overallRating = (ratings.clearBrief + ratings.responsive + ratings.respectful) / 3;

      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);
      if (!orderSnap.exists()) return rejectWithValue('Order not found');
      const orderData = orderSnap.data();
      if (!areOrderReviewsAllowed(orderData)) return rejectWithValue('Reviews are disabled for this order.');
      const reviewerPhotoURL = await getUserProfilePhotoURL(orderData.sellerUid, orderData.sellerPhotoURL);

      await setDoc(
        orderRef,
        {
          sellerReview: {
            ratings,
            comments,
            skillsets: skillsets || [],
            overallRating,
            reviewerUid: orderData.sellerUid || '',
            reviewerName: orderData.sellerName || '',
            reviewerPhotoURL,
            created: Timestamp.now()
          }
        },
        { merge: true }
      );

      const updatedOrderSnap = await getDoc(orderRef);
      if (!updatedOrderSnap.exists()) return rejectWithValue('Order not found');
      const updatedOrderData = updatedOrderSnap.data();
      const { buyerUid } = updatedOrderData;

      const userDataRef = doc(db, 'users', buyerUid, 'data', 'userdata');
      const userDataSnap = await getDoc(userDataRef);
      if (!userDataSnap.exists()) return rejectWithValue('Buyer user data not found');
      const userData = userDataSnap.data();

      const currentTotalVotes = userData.buyerTotalAmount || 0;
      const currentRatingSum = (userData.buyerTotalRating || 0) * currentTotalVotes;
      const newRatingSum = currentRatingSum + overallRating;
      const newTotalAmount = currentTotalVotes + 1;
      const newAverageRating = newRatingSum / newTotalAmount;

      await setDoc(
        userDataRef,
        { buyerTotalAmount: newTotalAmount, buyerTotalRating: newAverageRating },
        { merge: true }
      );

      const requiresBuyerReview = areOrderReviewsAllowed(updatedOrderData);
      const reviewUpdates = requiresBuyerReview && !updatedOrderData.enhancedReview
        ? {}
        : { status: 'completed', reviewCompletedAt: Timestamp.now(), activityTime: Timestamp.now() };

      if (Object.keys(reviewUpdates).length > 0) {
        await setDoc(orderRef, reviewUpdates, { merge: true });
      }

      return { sellerReview: updatedOrderData.sellerReview, orderId: orderId, ...reviewUpdates };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Enhanced review system with multiple rating categories
export const submitEnhancedReview = createAsyncThunk(
  'orders/submitEnhancedReview',
  async ({ orderId, ratings, comments, skillsets }, { dispatch, getState, rejectWithValue }) => {
    try {
      const overallRating = (ratings.serviceAsRequested + ratings.serviceOnTime + ratings.serviceWithCare) / 3;

      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);
      if (!orderSnap.exists()) return rejectWithValue('Order not found.');

      const orderData = orderSnap.data();
      if (!areOrderReviewsAllowed(orderData)) return rejectWithValue('Reviews are disabled for this order.');
      const reviewerPhotoURL = await getUserProfilePhotoURL(orderData.buyerUid, orderData.buyerPhotoURL);

      await setDoc(
        orderRef,
        {
          enhancedReview: {
            ratings: ratings,
            comments: comments,
            skillsets: skillsets || [],
            overallRating: overallRating,
            reviewerUid: orderData.buyerUid || '',
            reviewerName: orderData.buyerName || '',
            reviewerPhotoURL,
            created: Timestamp.now()
          }
        },
        { merge: true }
      );

      // Fetch the updated order to retrieve sellerUid
      const updatedOrderSnap = await getDoc(orderRef);
      const updatedOrderData = updatedOrderSnap.data();
      const { sellerUid } = updatedOrderData;

      // Retrieve the seller's user data
      const userDataRef = doc(db, 'users', sellerUid, 'data', 'userdata');
      const userDataSnap = await getDoc(userDataRef);
      const userData = userDataSnap.data();

      const currentTotalVotes = userData.sellerTotalAmount || 0;
      const currentRatingSum = (userData.sellerTotalRating || 0) * currentTotalVotes;
      const newRatingSum = currentRatingSum + overallRating;
      const newTotalAmount = currentTotalVotes + 1;
      const newAverageRating = newRatingSum / newTotalAmount;

      await setDoc(
        userDataRef,
        {
          sellerTotalAmount: newTotalAmount,
          sellerTotalRating: newAverageRating
        },
        { merge: true }
      );

      const reviewUpdates = updatedOrderData.sellerReview
        ? { status: 'completed', reviewCompletedAt: Timestamp.now(), activityTime: Timestamp.now() }
        : {};

      if (Object.keys(reviewUpdates).length > 0) {
        await setDoc(orderRef, reviewUpdates, { merge: true });
      }

      return { enhancedReview: updatedOrderData.enhancedReview, orderId: orderId, ...reviewUpdates };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const addNoteToOrder = createAsyncThunk(
  'orders/addNoteToOrder',
  async ({ orderId, note }) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      setDoc(orderRef, { note: note }, { merge: true });
      return { note: note, orderId: orderId };
    } catch (error) {
      return error;
    }
  }
);

export const changePaidStatus = createAsyncThunk(
  'orders/changePaidStatus',
  async ({ orderId, status }) => {
    try {
      const orderRef = doc(db, 'orders', orderId);
      setDoc(orderRef, { paid: status }, { merge: true });
      return { paid: status, orderId: orderId };
    } catch (error) {
      return error;
    }
  }
);

export const removeBooking = createAsyncThunk('orders/removeBooking', async (orderId) => {
  try {
    const orderRef = doc(db, 'orders', orderId);
    await deleteDoc(orderRef);
    return orderId;
  } catch (error) {
    return error;
  }
});

export const deleteJobAndOrders = createAsyncThunk(
  'jobs/deleteJobAndOrders',
  async (jobId, { dispatch }) => {
    try {
      // Delete the job by jobId
      const jobRef = doc(db, 'jobs', jobId);
      await deleteDoc(jobRef);

      // Find and delete related orders, but only if their status is 'pending'
      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, where('jobId', '==', jobId));
      const ordersSnapshot = await getDocs(q);

      // Iterate through each order, only delete if status is 'pending'
      const batchDeletePromises = ordersSnapshot.docs.map((orderDoc) => {
        const orderData = orderDoc.data();
        if (orderData.status === 'pending') {
          return deleteDoc(orderDoc.ref); // Only delete if the order has the correct status
        }
        return Promise.resolve(); // Do not delete other orders
      });

      await Promise.all(batchDeletePromises);

      console.log('Job and associated pending orders deleted successfully');
      return { jobId };
    } catch (error) {
      console.error('Error deleting job and associated orders:', error);
      throw error;
    }
  }
);

export const importCalendarEvents = createAsyncThunk(
  'orders/importCalendarEvents',
  async ({ events, userUid, calendarName }) => {
    try {
      const ref = collection(db, 'orders');
      const q = query(ref, where('sellerUid', '==', userUid));
      const snap = await getDocs(q);
      const existingEvents = [];
      snap.forEach((doc) => {
        const d = doc.data();
        existingEvents.push(d.importedEventUid || `${d.jobHeadline}_${d.startTime}_${d.date?.seconds}`);
      });

      const createdOrders = [];
      let skipped = 0;
      const name = calendarName?.trim() || 'Unnamed Calendar';

      for (const event of events) {
        const startDate = event.dtstart ? Timestamp.fromDate(new Date(event.dtstart)) : Timestamp.now();
        const startTime = event.dtstart ? new Date(event.dtstart).toTimeString().slice(0, 5) : '';
        const eventKey = event.importedEventUid || `${event.summary || 'Imported Event'}_${startTime}_${startDate.seconds}`;

        if (existingEvents.includes(eventKey)) {
          skipped++;
          continue;
        }

        const orderRef = doc(collection(db, 'orders'));
        const orderData = {
          orderId: orderRef.id,
          jobHeadline: event.summary || 'Imported Event',
          jobTitle: event.summary || 'Imported Event',
          description: event.description || '',
          buyerLocation: event.location || '',
          date: startDate,
          startDate: startDate,
          endDate: event.dtend ? Timestamp.fromDate(new Date(event.dtend)) : Timestamp.now(),
          startTime: startTime,
          endTime: event.dtend ? new Date(event.dtend).toTimeString().slice(0, 5) : '',
          sellerUid: userUid,
          buyerUid: '',
          buyerName: 'Imported',
          status: 'imported',
          confirmed: true,
          created: Timestamp.now(),
          calendarName: name,
          importedEventUid: eventKey,
          importedSourceUid: event.uid || '',
          note: `Imported from calendar: ${event.uid || ''}`,
        };
        await setDoc(orderRef, orderData);
        createdOrders.push(orderData);
        existingEvents.push(eventKey);
      }
      return { createdOrders, skipped };
    } catch (error) {
      console.error('Error importing calendar events:', error);
      throw error;
    }
  }
);

export const deleteImportedCalendar = createAsyncThunk(
  'orders/deleteImportedCalendar',
  async ({ userUid, calendarName }) => {
    const ref = collection(db, 'orders');
    const q = query(
      ref,
      where('sellerUid', '==', userUid),
      where('status', '==', 'imported'),
      where('calendarName', '==', calendarName)
    );
    const snap = await getDocs(q);
    await Promise.all(snap.docs.map((d) => deleteDoc(doc(db, 'orders', d.id))));
    return calendarName;
  }
);

const initialState = {
  bookings: [],
  currentSellerBookings: [],
  viewedSellerBookings: [],
  purchases: [],
  completedBookings: [],
  viewedSellerCompletedBookings: [],
  loading: false, // Add a loading flag
  jobsInProgress: 0,
  viewedSellerJobsInProgress: 0,
};

export const orderSlice = createSlice({
  name: 'orders',
  initialState: initialState,
  reducers: {
    resetOrder() {
      return initialState;
    },
    setBookings(state, action) {
      state.bookings = action.payload;
      state.currentSellerBookings = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      /* return a state with the booking array*/
      .addCase(fetchBookingsByQuery.fulfilled, (state, action) => {
        return (state = {
          ...state,
          bookings: action.payload,
          currentSellerBookings: action.payload
        });
      })
      .addCase(fetchViewedSellerBookings.pending, (state) => {
        state.viewedSellerBookings = [];
      })
      .addCase(fetchViewedSellerBookings.fulfilled, (state, action) => {
        return (state = {
          ...state,
          viewedSellerBookings: action.payload
        });
      })
      .addCase(fetchPurchasesByQuery.fulfilled, (state, action) => {
        return (state = {
          ...state,
          purchases: action.payload
        });
      })
      .addCase(fetchCompletedBookingsBySeller.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCompletedBookingsBySeller.fulfilled, (state, action) => {
        state.completedBookings = action.payload;
        state.loading = false;
      })
      .addCase(fetchCompletedBookingsBySeller.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchJobsInProgressBySeller.fulfilled, (state, action) => {
        state.jobsInProgress = action.payload;
      })
      .addCase(fetchViewedSellerCompletedBookings.pending, (state) => {
        state.viewedSellerCompletedBookings = [];
      })
      .addCase(fetchViewedSellerCompletedBookings.fulfilled, (state, action) => {
        state.viewedSellerCompletedBookings = action.payload;
      })
      .addCase(fetchViewedSellerJobsInProgress.pending, (state) => {
        state.viewedSellerJobsInProgress = 0;
      })
      .addCase(fetchViewedSellerJobsInProgress.fulfilled, (state, action) => {
        state.viewedSellerJobsInProgress = action.payload;
      })

      .addCase(changeConfirmedStatus.fulfilled, (state, action) => {
        let new_bookings = JSON.parse(JSON.stringify(state.bookings));
        let index = new_bookings.findIndex((f) => f.orderId === action.payload.orderId);
        new_bookings[index].confirmed = action.payload.confirmed; // make sure this is not undefined
        return (state = {
          ...state,
          bookings: new_bookings,
          currentSellerBookings: new_bookings
        });
      })
      .addCase(changeOrderStatus.fulfilled, (state, action) => {
        let new_bookings = JSON.parse(JSON.stringify(state.bookings));
        let index = new_bookings.findIndex((f) => f.orderId === action.payload.orderId);
        new_bookings[index].status = action.payload.status;
        new_bookings[index].activityTime = action.payload.activityTime;
        return (state = {
          ...state,
          bookings: new_bookings,
          currentSellerBookings: new_bookings
        });
      })
      .addCase(changePurchaseStatus.fulfilled, (state, action) => {
        let new_purchases = JSON.parse(JSON.stringify(state.purchases));
        let index = new_purchases.findIndex((f) => f.orderId === action.payload.orderId);
        if (index !== -1) {
          new_purchases[index].status = action.payload.status;
          new_purchases[index].activityTime = action.payload.activityTime;
          return (state = {
            ...state,
            purchases: new_purchases
          });
        }
        return state;
      })

      .addCase(rateCompletedOrder.fulfilled, (state, action) => {
        let new_bookings = [...state.bookings]; // Clone the bookings array
        let index = new_bookings.findIndex((order) => order.orderId === action.payload.orderId);

        // Ensure the order is found before attempting to set the seller review
        if (index !== -1) {
          new_bookings[index] = {
            ...new_bookings[index],
            sellerReview: action.payload.sellerReview,
            status: action.payload.status || new_bookings[index].status,
            reviewCompletedAt: action.payload.reviewCompletedAt || new_bookings[index].reviewCompletedAt,
            activityTime: action.payload.activityTime || new_bookings[index].activityTime
          };
          return {
            ...state,
            bookings: new_bookings,
            currentSellerBookings: new_bookings,
            purchases: state.purchases.map(o => o.orderId === action.payload.orderId ? {
              ...o,
              sellerReview: action.payload.sellerReview,
              status: action.payload.status || o.status,
              reviewCompletedAt: action.payload.reviewCompletedAt || o.reviewCompletedAt,
              activityTime: action.payload.activityTime || o.activityTime
            } : o)
          };
        } else {
          console.error('Order not found for given orderId:', action.payload.orderId);
          return state;
        }
      })

      .addCase(submitEnhancedReview.fulfilled, (state, action) => {
        let new_purchases = [...state.purchases]; // Clone the purchases array
        let index = new_purchases.findIndex((order) => order.orderId === action.payload.orderId);

        // Ensure the order is found before attempting to set the enhanced review
        if (index !== -1) {
          new_purchases[index] = {
            ...new_purchases[index],
            enhancedReview: action.payload.enhancedReview,
            status: action.payload.status || new_purchases[index].status,
            reviewCompletedAt: action.payload.reviewCompletedAt || new_purchases[index].reviewCompletedAt,
            activityTime: action.payload.activityTime || new_purchases[index].activityTime
          };
          return {
            ...state,
            purchases: new_purchases,
            bookings: state.bookings.map(o => o.orderId === action.payload.orderId ? {
              ...o,
              enhancedReview: action.payload.enhancedReview,
              status: action.payload.status || o.status,
              reviewCompletedAt: action.payload.reviewCompletedAt || o.reviewCompletedAt,
              activityTime: action.payload.activityTime || o.activityTime
            } : o),
            currentSellerBookings: state.currentSellerBookings.map(o => o.orderId === action.payload.orderId ? {
              ...o,
              enhancedReview: action.payload.enhancedReview,
              status: action.payload.status || o.status,
              reviewCompletedAt: action.payload.reviewCompletedAt || o.reviewCompletedAt,
              activityTime: action.payload.activityTime || o.activityTime
            } : o)
          };
        } else {
          console.error('Order not found for given orderId:', action.payload.orderId);
          return state;
        }
      })

      .addCase(addNoteToOrder.fulfilled, (state, action) => {
        let new_bookings = JSON.parse(JSON.stringify(state.bookings));
        let index = new_bookings.findIndex((f) => f.orderId === action.payload.orderId);
        new_bookings[index].note = action.payload.note;
        return (state = {
          ...state,
          bookings: new_bookings,
          currentSellerBookings: new_bookings
        });
      })
      .addCase(changePaidStatus.fulfilled, (state, action) => {
        let new_bookings = JSON.parse(JSON.stringify(state.bookings));
        let index = new_bookings.findIndex((f) => f.orderId === action.payload.orderId);
        if (index !== -1) {
          new_bookings[index].paid = action.payload.paid;
        } else {
          console.warn('Order not found for changePaidStatus:', action.payload.orderId);
        }
        return (state = {
          ...state,
          bookings: new_bookings,
          currentSellerBookings: new_bookings
        });
      })

      // ── New status-machine reducer cases ────────────────────────────────
      .addCase(acceptOrder.fulfilled, (state, action) => {
        const { orderId, ...updates } = action.payload;
        const updateOrder = (o) => o.orderId === orderId ? { ...o, ...updates } : o;
        return { ...state, bookings: state.bookings.map(updateOrder), currentSellerBookings: state.currentSellerBookings.map(updateOrder), purchases: state.purchases.map(updateOrder) };
      })
      .addCase(acceptOrderLimited.fulfilled, (state, action) => {
        const { orderId, declinedOrderIds = [], conflictedOrders = [], ...updates } = action.payload;
        const declinedSet  = new Set(declinedOrderIds);
        const conflictMap  = new Map(conflictedOrders.map((c) => [c.orderId, c]));
        const applyUpdates = (o) => {
          if (o.orderId === orderId) return { ...o, ...updates };
          if (declinedSet.has(o.orderId)) return { ...o, status: 'declined' };
          if (conflictMap.has(o.orderId)) {
            const { bufferConflict, conflictsWith } = conflictMap.get(o.orderId);
            return { ...o, bufferConflict, conflictsWith };
          }
          return o;
        };
        return {
          ...state,
          bookings:  state.bookings.map(applyUpdates),
          currentSellerBookings: state.currentSellerBookings.map(applyUpdates),
          purchases: state.purchases.map(applyUpdates),
        };
      })
      .addCase(declineOrder.fulfilled, (state, action) => {
        const { orderId, ...updates } = action.payload;
        const updateOrder = (o) => o.orderId === orderId ? { ...o, ...updates } : o;
        return { ...state, bookings: state.bookings.map(updateOrder), currentSellerBookings: state.currentSellerBookings.map(updateOrder), purchases: state.purchases.map(updateOrder) };
      })
      .addCase(autoDeclineExpiredOrders.fulfilled, (state, action) => {
        const declinedIds = new Set(action.payload);
        const updateOrder = (o) => declinedIds.has(o.orderId) ? { ...o, status: 'declined' } : o;
        return { ...state, bookings: state.bookings.map(updateOrder), currentSellerBookings: state.currentSellerBookings.map(updateOrder) };
      })
      .addCase(markStarted.fulfilled, (state, action) => {
        const { orderId, ...updates } = action.payload;
        const updateOrder = (o) => o.orderId === orderId ? { ...o, ...updates } : o;
        return { ...state, bookings: state.bookings.map(updateOrder), currentSellerBookings: state.currentSellerBookings.map(updateOrder), purchases: state.purchases.map(updateOrder) };
      })
      .addCase(markCompleted.fulfilled, (state, action) => {
        const { orderId, ...updates } = action.payload;
        const updateOrder = (o) => o.orderId === orderId ? { ...o, ...updates } : o;
        return { ...state, bookings: state.bookings.map(updateOrder), currentSellerBookings: state.currentSellerBookings.map(updateOrder), purchases: state.purchases.map(updateOrder) };
      })
      .addCase(requestCancel.fulfilled, (state, action) => {
        const { orderId, ...updates } = action.payload;
        const updateOrder = (o) => o.orderId === orderId ? { ...o, ...updates } : o;
        return { ...state, bookings: state.bookings.map(updateOrder), currentSellerBookings: state.currentSellerBookings.map(updateOrder), purchases: state.purchases.map(updateOrder) };
      })
      .addCase(cancelBooking.fulfilled, (state, action) => {
        const { orderId, ...updates } = action.payload;
        const updateOrder = (o) => o.orderId === orderId ? { ...o, ...updates } : o;
        return { ...state, bookings: state.bookings.map(updateOrder), currentSellerBookings: state.currentSellerBookings.map(updateOrder), purchases: state.purchases.map(updateOrder) };
      })
      .addCase(allowCancel.fulfilled, (state, action) => {
        const { orderId, ...updates } = action.payload;
        const updateOrder = (o) => o.orderId === orderId ? { ...o, ...updates } : o;
        return { ...state, bookings: state.bookings.map(updateOrder), currentSellerBookings: state.currentSellerBookings.map(updateOrder), purchases: state.purchases.map(updateOrder) };
      })
      .addCase(denyCancel.fulfilled, (state, action) => {
        const { orderId, ...updates } = action.payload;
        const updateOrder = (o) => o.orderId === orderId ? { ...o, ...updates } : o;
        return { ...state, bookings: state.bookings.map(updateOrder), currentSellerBookings: state.currentSellerBookings.map(updateOrder), purchases: state.purchases.map(updateOrder) };
      })
      .addCase(cancelOrder.fulfilled, (state, action) => {
        const { orderId, ...updates } = action.payload;
        const updateOrder = (o) => o.orderId === orderId ? { ...o, ...updates } : o;
        return { ...state, bookings: state.bookings.map(updateOrder), currentSellerBookings: state.currentSellerBookings.map(updateOrder), purchases: state.purchases.map(updateOrder) };
      })
      .addCase(confirmPayment.fulfilled, (state, action) => {
        const { orderId, ...updates } = action.payload;
        const updateOrder = (o) => o.orderId === orderId ? { ...o, ...updates } : o;
        return { ...state, bookings: state.bookings.map(updateOrder), currentSellerBookings: state.currentSellerBookings.map(updateOrder), purchases: state.purchases.map(updateOrder) };
      })
      .addCase(reportPayment.fulfilled, (state, action) => {
        const { orderId, ...updates } = action.payload;
        const updateOrder = (o) => o.orderId === orderId ? { ...o, ...updates } : o;
        return { ...state, bookings: state.bookings.map(updateOrder), currentSellerBookings: state.currentSellerBookings.map(updateOrder), purchases: state.purchases.map(updateOrder) };
      })
      .addCase(archiveOrder.fulfilled, (state, action) => {
        const { orderId, ...updates } = action.payload;
        return { ...state, purchases: state.purchases.map(o => o.orderId === orderId ? { ...o, ...updates } : o) };
      })

      .addCase(removeBooking.fulfilled, (state, action) => {
        return {
          ...state,
          bookings: state.bookings.filter((booking) => booking.orderId !== action.payload),
          currentSellerBookings: state.currentSellerBookings.filter((booking) => booking.orderId !== action.payload)
        };
      })
      .addCase(deleteJobAndOrders.fulfilled, (state, action) => {
        return {
          ...state,
          bookings: state.bookings.filter((booking) => booking.jobId !== action.payload.jobId),
          currentSellerBookings: state.currentSellerBookings.filter((booking) => booking.jobId !== action.payload.jobId)
        };
      })

      .addCase(applyLateFeeToOrder.fulfilled, (state, action) => {
        let new_bookings = JSON.parse(JSON.stringify(state.bookings));
        let index = new_bookings.findIndex((f) => f.orderId === action.payload.orderId);
        if (index !== -1 && action.payload.lateFeeApplied) {
          new_bookings[index].lateFeeApplied = true;
          new_bookings[index].lateFeeAmount = action.payload.lateFeeAmount;
          new_bookings[index].lateFeeAppliedAt = action.payload.lateFeeAppliedAt;
          new_bookings[index].totalWithLateFee = action.payload.totalWithLateFee;
          return (state = {
            ...state,
            bookings: new_bookings,
            currentSellerBookings: new_bookings
          });
        }
        return state;
      })
      .addCase(checkAndApplyLateFees.fulfilled, (state, action) => {
        // Update multiple orders with late fees
        let new_bookings = JSON.parse(JSON.stringify(state.bookings));
        let updated = false;
        
        action.payload.forEach(result => {
          if (result.lateFeeApplied) {
            let index = new_bookings.findIndex((f) => f.orderId === result.orderId);
            if (index !== -1) {
              new_bookings[index].lateFeeApplied = true;
              new_bookings[index].lateFeeAmount = result.lateFeeAmount;
              new_bookings[index].lateFeeAppliedAt = result.lateFeeAppliedAt;
              new_bookings[index].totalWithLateFee = result.totalWithLateFee;
              updated = true;
            }
          }
        });
        
        if (updated) {
          return (state = {
            ...state,
            bookings: new_bookings,
            currentSellerBookings: new_bookings
          });
        }
        return state;
      })
      .addCase(importCalendarEvents.fulfilled, (state, action) => {
        const bookings = [...state.bookings, ...action.payload.createdOrders];
        return {
          ...state,
          bookings,
          currentSellerBookings: bookings
        };
      })
      .addCase(deleteImportedCalendar.fulfilled, (state, action) => {
        const bookings = state.bookings.filter(
          (b) => !(b.status === 'imported' && b.calendarName === action.payload)
        );
        return {
          ...state,
          bookings,
          currentSellerBookings: bookings
        };
      });
  }
});

export const { resetOrder, setBookings } = orderSlice.actions;
export default orderSlice.reducer;


